'use strict'

const { flattenCardFields } = require('./card-layout')

// Columns stored as a single comparable value on the record. Relationship,
// image, file, and password fields are not ordered as record columns.
const SORTABLE_TYPES = new Set(['text', 'number', 'dateTime', 'formula', 'richText', 'multiselect', 'duration', 'sequence'])

const SYSTEM_SORT_COLUMNS = [
  { column: 'id', title: 'ID' },
  { column: 'createdAt', title: 'Created At' },
  { column: 'updatedAt', title: 'Updated At' }
]

function isSortableColumn (column) {

  if (!column || !column.uniqueTitle || !column.type) return false

  return SORTABLE_TYPES.has(column.type)
}

function sortColumnOptions (columns) {

  const options = SYSTEM_SORT_COLUMNS.map((column) => ({
    value: column.column,
    text: column.title
  }))
  const seen = new Set(options.map((option) => option.value))

  for (const column of columns || []) {

    if (!isSortableColumn(column) || seen.has(column.uniqueTitle)) continue

    options.push({
      value: column.uniqueTitle,
      text: column.title || column.uniqueTitle
    })
    seen.add(column.uniqueTitle)
  }

  return options
}

function sanitizeSorts (sorts) {

  if (!Array.isArray(sorts)) return []

  const sanitized = []
  const seen = new Set()

  for (const sort of sorts) {

    if (!sort || typeof sort.column !== 'string' || !sort.column || seen.has(sort.column)) continue

    sanitized.push({
      column: sort.column,
      direction: sort.direction === 'desc' ? 'desc' : 'asc'
    })
    seen.add(sort.column)
  }

  return sanitized
}

function normalizeSorts (sorts, columns) {

  const allowed = new Set(sortColumnOptions(columns).map((option) => option.value))

  return sanitizeSorts(sorts).filter((sort) => allowed.has(sort.column))
}

// User-chosen columns come first. Later duplicates (including the view's
// saved sorts) are dropped so each column is ordered once.
function mergeSorts (primary, secondary) {

  return sanitizeSorts([].concat(primary || [], secondary || []))
}

// Header clicks win, unless the view has turned user sorting off.
// A column marked disableSorting is left out of the header sort, but a
// saved view sort on that column still applies.
function sortsForQuery (component, query) {

  if (!component || component.disableUserSorting) return component && component.sorts

  const blocked = new Set()

  for (const column of flattenCardFields(component.components)) {

    if (column && column.disableSorting && column.uniqueTitle) blocked.add(column.uniqueTitle)
  }

  const userSorts = (query && query.userSorts) || []
  const allowed = Array.isArray(userSorts)
    ? userSorts.filter((sort) => sort && !blocked.has(sort.column))
    : []

  return mergeSorts(allowed, component && component.sorts)
}

function isColumnUserSortable (table, column) {

  if (!table || table.disableUserSorting) return false
  if (!column || column.disableSorting) return false
  if (column.relatedField) return false

  return true
}

// Map a table header sort (field key + order) to spreadsheet columns.
// A cleared header has no order and is omitted.
function userSortsFromHeader (sortBy, fields) {

  if (!Array.isArray(sortBy)) return []

  const sorts = []

  for (const entry of sortBy) {

    if (!entry || (entry.order !== 'asc' && entry.order !== 'desc')) continue

    const field = (fields || []).find((item) => item.key === entry.key)

    if (!field || !field.columnUniqueTitle || field.sortable === false) continue

    sorts.push({ column: field.columnUniqueTitle, direction: entry.order })
  }

  return sorts
}

module.exports = {
  SORTABLE_TYPES,
  SYSTEM_SORT_COLUMNS,
  isSortableColumn,
  sortColumnOptions,
  sanitizeSorts,
  normalizeSorts,
  mergeSorts,
  sortsForQuery,
  userSortsFromHeader,
  isColumnUserSortable
}
